/**
 * A draft in the shapes its two readers need: the JSON the database takes,
 * and the per-guard weekly hours the review step sets against each contract.
 */

import { windowMinutes } from '../rosterCompliance/rosterModel';
import { fromDayNumber, toDayNumber, weekStartDayNumber } from './time';
import type {
  ContractStanding,
  DayDemand,
  DraftTotals,
  GuardWeekSummary,
  PlannedAssignment,
  ScheduleContext,
  ScheduleGuard,
  ScheduleOptions,
  SchedulePlanPayload,
  UnfilledBlock,
} from './types';

/**
 * Every new post goes in, filled or not: an open post is how the board shows
 * cover that is still missing, and the next run can fill it.
 */
export function toPlanPayload(days: DayDemand[], assignments: PlannedAssignment[]): SchedulePlanPayload {
  return {
    slots: days
      .flatMap((day) => day.blocks)
      .filter((block) => block.isNewSlot)
      .map((block) => ({
        id: block.slotId,
        site_id: block.siteId,
        slot_date: block.slotDate,
        start_minute: block.startMinute,
        end_minute: block.endMinute,
        lane: block.lane,
        colour: block.colour,
      })),
    assignments: assignments.map((assignment) => ({
      duty_slot_id: assignment.block.slotId,
      guard_id: assignment.guardId,
    })),
    breaks: assignments.flatMap((assignment) => (assignment.breakWindow ? [{
      site_id: assignment.block.siteId,
      slot_date: assignment.block.slotDate,
      guard_id: assignment.guardId,
      start_minute: assignment.breakWindow.startMinute,
      end_minute: assignment.breakWindow.endMinute,
    }] : [])),
  };
}

export function draftTotals(
  days: DayDemand[],
  assignments: PlannedAssignment[],
  unfilled: UnfilledBlock[],
): DraftTotals {
  return {
    assignedShifts: assignments.length,
    assignedMinutes: assignments.reduce((total, assignment) => total + windowMinutes(assignment.block), 0),
    guardsUsed: new Set(assignments.map((assignment) => assignment.guardId)).size,
    openShifts: unfilled.length,
    newPosts: days.reduce((total, day) => total + day.blocks.filter((block) => block.isNewSlot).length, 0),
  };
}

export interface SiteDraftTotals extends DraftTotals {
  siteId: string;
  /** Days of the period with something to draft at this site. */
  plannedDays: number;
  /** Days left alone: past, published, or already fully rostered. */
  skippedDays: number;
}

/** The draft's totals one site at a time, in the order the sites are listed. */
export function totalsBySite(
  siteIds: string[],
  days: DayDemand[],
  assignments: PlannedAssignment[],
  unfilled: UnfilledBlock[],
): SiteDraftTotals[] {
  return siteIds.map((siteId) => {
    const siteDays = days.filter((day) => day.siteId === siteId);
    return {
      siteId,
      ...draftTotals(
        siteDays,
        assignments.filter((assignment) => assignment.block.siteId === siteId),
        unfilled.filter((open) => open.block.siteId === siteId),
      ),
      plannedDays: siteDays.filter((day) => day.status === 'planned').length,
      skippedDays: siteDays.filter((day) => day.status !== 'planned').length,
    };
  });
}

function standingOf(guard: ScheduleGuard, totalMinutes: number): ContractStanding {
  if (guard.contractMinWeeklyMinutes === null) return 'no_contract';
  if (totalMinutes < guard.contractMinWeeklyMinutes) return 'below_minimum';
  if (guard.contractMaxWeeklyMinutes !== null && totalMinutes > guard.contractMaxWeeklyMinutes) return 'above_band';
  return 'within_band';
}

const STANDING_ORDER: ContractStanding[] = ['below_minimum', 'above_band', 'no_contract', 'within_band'];

/**
 * Hours against contract for each week the draft touches. A guard appears if
 * the draft gave them work that week, or if the week is settled and still
 * leaves them short of their minimum -- the case an admin has to know about.
 */
export function summariseWeeks(
  context: ScheduleContext,
  options: ScheduleOptions,
  days: DayDemand[],
  assignments: PlannedAssignment[],
): GuardWeekSummary[] {
  const periodStart = toDayNumber(options.startDate);
  const periodEnd = toDayNumber(options.endDate);
  // Sites in different timezones can be on different dates; the earliest one
  // decides which days can still be rostered.
  const today = Math.min(...context.sites.map((site) => toDayNumber(site.today)));

  const mondays = Array.from(new Set(
    days.filter((day) => day.status !== 'past').map((day) => weekStartDayNumber(toDayNumber(day.date))),
  )).sort((a, b) => a - b);

  const summaries: GuardWeekSummary[] = [];
  mondays.forEach((monday) => {
    // A day outside the draft that has not happened yet can still be rostered.
    const weekSettled = Array.from({ length: 7 }, (_, index) => monday + index)
      .every((day) => (day >= periodStart && day <= periodEnd) || day < today);

    context.guards.forEach((guard) => {
      const inWeek = (dateKey: string): boolean => {
        const day = toDayNumber(dateKey);
        return day >= monday && day <= monday + 6;
      };
      const existingMinutes = guard.shifts
        .filter((shift) => inWeek(shift.slotDate))
        .reduce((total, shift) => total + shift.dutyMinutes, 0);
      const draftedMinutes = assignments
        .filter((assignment) => assignment.guardId === guard.id && inWeek(assignment.block.slotDate))
        .reduce((total, assignment) => total + windowMinutes(assignment.block), 0);

      const standing = standingOf(guard, existingMinutes + draftedMinutes);
      if (draftedMinutes === 0 && !(weekSettled && standing === 'below_minimum')) return;

      summaries.push({
        guardId: guard.id,
        guardName: guard.fullName,
        weekStart: fromDayNumber(monday),
        existingMinutes,
        draftedMinutes,
        contractMinWeeklyMinutes: guard.contractMinWeeklyMinutes,
        contractMaxWeeklyMinutes: guard.contractMaxWeeklyMinutes,
        standing,
        weekSettled,
      });
    });
  });

  return summaries.sort((a, b) => (
    a.weekStart.localeCompare(b.weekStart)
    || STANDING_ORDER.indexOf(a.standing) - STANDING_ORDER.indexOf(b.standing)
    || a.guardName.localeCompare(b.guardName)
    || a.guardId.localeCompare(b.guardId)
  ));
}
